import { useCallback, useEffect, useState } from "react";
import { gameName } from "../../lib/gameName";
import { useNavigate } from "react-router-dom";
import { Clock, Trophy, Users, Ticket, Star } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";

const pad = (n) => String(n).padStart(2, "0");
const until = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  return h ? `${h}h ${pad(Math.floor((s % 3600) / 60))}m` : `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
};
// "20:00" → "8.00pm"; every start time is written in UTC+10 (the room's clock).
const clockLabel = (hhmm) => {
  const [h, m = "00"] = String(hhmm).split(":");
  const hour = Number(h);
  return `${hour % 12 || 12}.${String(m).padStart(2, "0")}${hour < 12 ? "am" : "pm"}`;
};

// Tournament lobby: the daily freerolls and any scheduled MTT. Registration happens here — seats are dealt by the server.
export const MttList = ({ onBalances, bare = false, game = "ALL", onGames }) => {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [, tick] = useState(0);
  // club tournaments live in their own club lobby, not the main room
  const load = useCallback(() => api.get(C.ApiRoutes.MTT).then((r) => setRows(r.data.filter((ev) => !ev.event_id.startsWith("club-")))).catch(() => {}), []);
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(i); }, []);
  useEffect(() => { onGames?.(rows.map((r) => r.game)); }, [rows, onGames]);

  const act = async (ev, route, ok) => {
    setBusy(ev.event_id);
    try {
      await api.post(route.replace("{event_id}", ev.event_id));
      toast.success(ok);
      await load();
      onBalances?.();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  const shown = rows.filter((r) => game === "ALL" || r.game === game);
  if (!bare && !rows.length) return null;
  const list = (
      <div className="flex flex-col gap-3">
        {bare && !shown.length && <div className="text-sm text-slate-400 py-6 text-center" data-testid="mtt-empty">No tournaments scheduled right now — the daily freerolls appear here when the host opens them.</div>}
        {shown.map((ev) => {
          const inst = ev.instance;
          const mine = inst?.registered_me;
          const running = inst?.status === "RUNNING";
          const registering = inst?.status === "REGISTERING";
          const startIn = inst ? inst.start_at - Date.now() : (ev.next_at ? ev.next_at - Date.now() : null);
          const pool = inst ? inst.prize_pool : ev.free ? (ev.prize_pool_fixed || 0) + (ev.prize_per_entrant || 0) : 0;
          return (
            <div key={ev.event_id} data-testid={`mtt-row-${ev.event_id}`} className="gold-row rounded-xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-display text-sm text-amber-50 flex items-center gap-2">
                  {ev.name}
                  {ev.free && <span data-testid={`mtt-free-${ev.event_id}`} className="rounded-full border border-emerald-400/60 px-2 py-0.5 text-[9px] tracking-[0.2em] text-emerald-200">FREEROLL</span>}
                  {ev.affiliate_name && <span data-testid={`mtt-affiliate-${ev.event_id}`} className="rounded-full border border-amber-300/60 px-2 py-0.5 text-[9px] tracking-[0.2em] text-amber-200">AFFILIATE GAME · {ev.affiliate_name.toUpperCase()}</span>}
                  {registering && <span className="rounded-full border border-amber-400/60 px-2 py-0.5 text-[9px] tracking-[0.2em] text-amber-200 animate-pulse">REGISTERING</span>}
                  {running && <span className="rounded-full border border-cyan-400/60 px-2 py-0.5 text-[9px] tracking-[0.2em] text-cyan-200">LIVE · LEVEL {inst.level}</span>}
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>{gameName(ev)}</span>
                  <span>{ev.free ? "Free entry" : `${fmtChips(ev.buy_in)} + ${fmtChips(ev.fee)}`}</span>
                  {ev.free && ev.prize_pool_fixed > 0 && <span className="text-amber-200/90" data-testid={`mtt-guarantee-${ev.event_id}`}>{fmtChips(ev.prize_pool_fixed)} guaranteed{ev.prize_per_entrant > 0 ? ` + ${fmtChips(ev.prize_per_entrant)} per player` : ""}</span>}
                  <span>{fmtChips(ev.starting_stack)} stack · {ev.level_minutes}-min levels</span>
                  {ev.schedule?.times?.length > 0 && <span className="flex items-center gap-1" data-testid={`mtt-times-${ev.event_id}`}><Clock className="w-3 h-3" /> {ev.schedule.times.map(clockLabel).join(" · ")} daily ({ev.schedule.tz_label || "UTC+10"})</span>}
                  {ev.max_entrants > 0 && <span className="flex items-center gap-1 text-slate-400"><Users className="w-3 h-3" /> limited to {ev.max_entrants} players</span>}
                  {ev.vip_top3?.length > 0 && <span className="flex items-center gap-1 text-amber-200/80"><Star className="w-3 h-3" /> {ev.vip_top3.join("/")} VIP to the top {ev.vip_top3.length}</span>}
                </div>
                {inst && (
                  <div className="text-xs mt-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`mtt-state-${ev.event_id}`}>
                    <span className="flex items-center gap-1 text-slate-300"><Users className="w-3.5 h-3.5 gold-text" /> {running ? `${inst.remaining} of ${inst.registered} left` : `${inst.registered}${inst.max_entrants ? ` / ${inst.max_entrants}` : ""} registered`}</span>
                    <span className="text-amber-200/90 font-mono">Prize pool {fmtChips(pool)}</span>
                    {running && <span className="text-slate-400 font-mono">{inst.tables} table{inst.tables === 1 ? "" : "s"} · avg {fmtChips(inst.avg_stack)}</span>}
                    {registering && startIn != null && !inst.waiting_for_players && <span className="text-emerald-300 font-mono" data-testid={`mtt-countdown-${ev.event_id}`}>Starts in {until(startIn)}</span>}
                    {registering && inst.waiting_for_players && (
                      <span className="text-amber-200 font-mono" data-testid={`mtt-waiting-${ev.event_id}`}>
                        Waiting for players · needs {inst.min_entrants} · late entry until {until(inst.late_until - Date.now())} from now
                      </span>
                    )}
                    {running && inst.late_reg_open && <span className="text-emerald-300/90">Late registration open</span>}
                    {inst.status === "FINISHED" && inst.payouts?.length > 0 && <span className="text-amber-300">Won by {inst.payouts[0].name}</span>}
                  </div>
                )}
                {!inst && startIn != null && (
                  <div className="text-xs mt-2 text-slate-400" data-testid={`mtt-next-${ev.event_id}`}>Registration opens an hour before the start · next one in {until(startIn)}</div>
                )}
              </div>
              <div className="flex items-center gap-3">
                {mine && inst?.my_table_id && (
                  <button data-testid={`mtt-goto-${ev.event_id}`} onClick={() => nav(`/table/${inst.my_table_id}`)} className="btn-gold rounded-full px-5 py-2 text-sm font-display btn-lift">Table {inst.my_table_no}</button>
                )}
                {mine && registering && (
                  <button data-testid={`mtt-unregister-${ev.event_id}`} disabled={busy === ev.event_id} onClick={() => act(ev, C.ApiRoutes.MTT_UNREGISTER, "Unregistered — your buy-in is back")}
                    className="rounded-full px-5 py-2 text-xs bg-white/5 hover:bg-red-500/30 btn-lift">Unregister</button>
                )}
                {!mine && inst && (registering || (running && inst.late_reg_open)) && (
                  <button data-testid={`mtt-register-${ev.event_id}`} disabled={busy === ev.event_id} onClick={() => act(ev, C.ApiRoutes.MTT_REGISTER, running ? "You're in — taking your seat now" : "You're registered. Good luck!")}
                    className="btn-gold rounded-full px-6 py-2 text-sm font-display btn-lift flex items-center gap-2"><Ticket className="w-4 h-4" /> {running ? "Late register" : "Register"}</button>
                )}
                {!inst && <span className="text-xs text-slate-500">Not open yet</span>}
              </div>
            </div>
          );
        })}
      </div>
  );
  if (bare) return list;
  return (
    <div className="gold-card rounded-2xl p-6 rise" data-testid="mtt-card">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h2 className="text-base md:text-lg gold-text uppercase tracking-[0.2em] flex items-center gap-2"><Trophy className="w-4 h-4" /> Tournaments</h2>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Register here · seats are dealt at the start</span>
      </div>
      {list}
    </div>
  );
};
